import { JsPsychPlugin, ParameterType, TrialType } from "jspsych";

const info = <const>{
  name: "async-loader",
  parameters: {
    /**
     * Async function to run before the trial finishes. Receives no arguments;
     * its resolved value (if an object) is merged into the trial's data.
     */
    loadFn: {
      type: ParameterType.FUNCTION,
      pretty_name: "Load function",
      default: undefined,
    },
    /**
     * Optional background HTML shown behind the loading indicator (e.g. the
     * normal game board markup), so the wait doesn't look like it left the game.
     */
    stimulus: {
      type: ParameterType.HTML_STRING,
      pretty_name: "Stimulus",
      default: "",
    },
    /**
     * Optional callback run right after `stimulus` is inserted into the DOM
     * (before `loadFn` starts), for callers that need to do DOM-dependent
     * setup on the freshly-rendered background (e.g. positioning game board
     * elements the same way a real trial would).
     */
    afterRender: {
      type: ParameterType.FUNCTION,
      pretty_name: "After render",
      default: undefined,
    },
  },
};

type Info = typeof info;

/**
 * **async-loader**
 *
 * Invisible-ish trial that awaits an async `loadFn` before advancing the
 * timeline. Renders `stimulus` (typically the same board markup as a normal
 * trial, frozen/idle) with a small loading indicator over it, so the wait doesn't look like it left the game.
 */
class CustomAsyncLoaderPlugin implements JsPsychPlugin<Info> {
  static info = info;

  constructor(private jsPsych) {}

  async trial(display_element: HTMLElement, trial: TrialType<Info>) {
    const _rawStimulus: unknown = trial.stimulus;
    const stimulusHtml: string =
      typeof _rawStimulus === "function" ? (_rawStimulus as () => string)() : (_rawStimulus as string) || "";
    display_element.innerHTML = `
      ${stimulusHtml}
      <div class="gear-async-loader-overlay">
        <div class="gear-async-loader-spinner"></div>
      </div>
    `;
    if (typeof trial.afterRender === "function") trial.afterRender(display_element);
    const result = typeof trial.loadFn === "function" ? await trial.loadFn() : undefined;
    this.jsPsych.finishTrial(result && typeof result === "object" ? result : {});
  }
}

export default CustomAsyncLoaderPlugin;
